import { tenantPb } from "@/lib/pb/clients"

/**
 * Live updates from the business's own server.
 *
 * PocketBase pushes record changes over SSE, one stream per client, with a
 * subscription per collection. Unlike the Django Channels socket it replaces,
 * there is no token to smuggle into a cookie: the SDK sends the session it
 * already holds, and the collection's own rules decide what comes back — a
 * user only ever sees changes to rows they could have read anyway.
 */

/**
 * Collections worth watching, and nothing else.
 *
 * Subscribing to a collection that does not exist yet is an error, so this
 * list grows as the tenant side of the migration does. A failed subscription
 * is skipped rather than fatal for exactly that reason.
 */
const WATCHED = [
  "plan_snapshots",
  "customers",
  "services",
  "class_types",
  "class_schedules",
  "class_sessions",
  "class_bookings",
  "waitlist_entries",
  "appointments",
  "service_categories",
  "users",
  "availability",
  "business_profile",
  "booking_settings",
  "notification_settings",
  "audit_log",
] as const

let connected = false
const listeners = new Set<() => void>()

function setConnected(value: boolean): void {
  if (connected === value) return
  connected = value
  for (const listener of listeners) listener()
}

/** The subscribe half of `useSyncExternalStore`; `realtimeStatus` is the getter. */
export function onRealtimeStatus(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function realtimeStatus(): boolean {
  return connected
}

/**
 * Watches every collection this app draws, reporting which one changed.
 *
 * The record in the event is deliberately ignored. It is the row as the rules
 * let this user see it, which is not always the shape a screen asked for, so
 * the change is treated as a hint to refetch rather than data to trust.
 */
export async function startRealtime(
  onChange: (collection: string) => void,
): Promise<() => void> {
  const unsubscribers: Array<() => void> = []

  tenantPb.realtime.onDisconnect = () => setConnected(false)

  // Asked for together rather than one after another. The SDK gathers every
  // subscription made in the same tick and sends the whole list in one request;
  // waiting for each in turn puts a tick between them, so the page opened with
  // nine round trips where one would do. The stream itself was always single.
  const opened = await Promise.allSettled(
    WATCHED.map((collection) =>
      tenantPb.collection(collection).subscribe("*", (event) => {
        setConnected(true)
        onChange(event.record?.collectionName ?? collection)
      }),
    ),
  )

  for (const attempt of opened) {
    // A collection that has not been built yet, or that this user may not read.
    // Neither is worth losing the others over.
    if (attempt.status !== "fulfilled") continue
    unsubscribers.push(attempt.value)
    setConnected(true)
  }

  return () => {
    for (const stop of unsubscribers) void stop()
    setConnected(false)
  }
}

/** Drops the stream, e.g. when switching business or signing out. */
export function stopRealtime(): void {
  void tenantPb.realtime.unsubscribe()
  setConnected(false)
}
